import {
	type ReactNode,
	type RefCallback,
	type RefObject,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
} from 'react';

import { functionWithFG } from '@atlaskit/platform-feature-flags-react';
import { useMenuItemDragAndDrop } from '@atlaskit/side-nav-items/drag-and-drop/use-menu-item-drag-and-drop';

import { useElementRegistry } from '../../controllers/element-registry';
import type { ItemDragAndDropGroup, NavItemConfigWithCustomizationOptions } from '../../types';
import {
	type GetItemData,
	type IsItemData,
	type IsItemDataInGroup,
	type IsItemDraggable,
	type IsItemDroppable,
	type ItemData,
} from '../dnd-data';
import { getDragPreviewContentForItem, getElemBeforeForItem } from '../get-drag-preview';
import type { AnalyticsEventHandler } from '../use-item-drop';

interface ItemDragAndDrop {
	draggableRef: RefCallback<HTMLAnchorElement | HTMLButtonElement> | undefined;
	dropIndicator: ReactNode | undefined;
	dropTargetRef: RefObject<HTMLDivElement> | undefined;
	isDragAndDropEnabled?: boolean;
	isDragging: boolean;
	isItemDraggable: boolean;
	isItemDroppable: boolean;
	maybeDragPreview: ReactNode;
}

interface Props<T extends ItemData> {
	data: T;
	fireAnalyticsEvent?: AnalyticsEventHandler;
	getItemData: GetItemData<T>;
	group: ItemDragAndDropGroup;
	id: string;
	isDragAndDropEnabled?: boolean;
	isExpandable: boolean;
	isExpandOrFlyoutOpen: boolean;
	isItemData: IsItemData<T>;
	isItemDataInGroup: IsItemDataInGroup<T>;
	isItemDraggable?: IsItemDraggable<T>;
	isItemDroppable?: IsItemDroppable<T>;
	item?: NavItemConfigWithCustomizationOptions;
	onExpandFlyoutToggle: (open: boolean) => void;
}

export const useItemDragAndDrop: typeof useItemDragAndDropOLD = functionWithFG(
	'navx-4339-fix-gsn-jira-ref-stability',
	useItemDragAndDropNEW,
	useItemDragAndDropOLD,
);

function useItemDragAndDropNEW<T extends ItemData>(props: Props<T>): ItemDragAndDrop {
	const {
		data,
		item,
		id,
		isExpandable,
		isItemDraggable,
		isItemDroppable,
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		isDragAndDropEnabled,
		group,
		isItemDataInGroup,
		isItemData,
		getItemData,
	} = props;
	const itemIsDraggable = isItemDraggable?.(data) ?? !!isDragAndDropEnabled;
	const itemIsDroppable = isItemDroppable?.(data) ?? !!isDragAndDropEnabled;

	const getInitialData = useCallback(
		() => getItemData({ ...data, menuItemId: id, group } as T),
		[data, getItemData, group, id],
	);

	const getDragPreviewPieces = useCallback(
		() => ({
			elemBefore: getElemBeforeForItem(item),
			content: getDragPreviewContentForItem(item, data?.label),
		}),
		[data?.label, item],
	);

	const canDrag = useCallback(
		() => (isItemDraggable ? isItemDraggable(data) : !!isDragAndDropEnabled),
		[data, isDragAndDropEnabled, isItemDraggable],
	);

	const getData = useCallback(
		(args: { source: { data: Record<string | symbol, unknown> } }) => {
			const { source } = args;
			return getItemData({
				...data,
				menuItemId: id,
				group: source?.data?.group as ItemDragAndDropGroup,
			} as T);
		},
		[data, getItemData, id],
	);

	const getOperations = useCallback(
		() => ({
			'reorder-before': itemIsDraggable ? ('available' as const) : undefined,
			'reorder-after': 'available' as const,
		}),
		[itemIsDraggable],
	);

	const canDrop = useCallback(
		(args: { source: { data: Record<string | symbol, unknown> } }) => {
			const { source } = args;

			return (
				isItemData(source.data) &&
				source.data.menuItemId !== id &&
				isItemDataInGroup(source.data, group)
			);
		},
		[group, id, isItemData, isItemDataInGroup],
	);
	const draggable = useMemo(() => {
		if (isDragAndDropEnabled && itemIsDraggable) {
			return { canDrag, getInitialData, getDragPreviewPieces };
		}
	}, [canDrag, getInitialData, getDragPreviewPieces, isDragAndDropEnabled, itemIsDraggable]);

	const dropTarget = useMemo(() => {
		if (isDragAndDropEnabled && itemIsDroppable) {
			return { canDrop, getData, getOperations };
		}
	}, [canDrop, getData, getOperations, isDragAndDropEnabled, itemIsDroppable]);

	const memoizedHookProps: Parameters<typeof useMenuItemDragAndDrop>[0] = useMemo(
		() => ({ draggable, dropTarget }),
		[draggable, dropTarget],
	);

	const {
		draggableAnchorRef,
		draggableButtonRef,
		state: dragState,
		dragPreview,
		dropTargetRef,
		dropIndicator,
	} = useMenuItemDragAndDrop(memoizedHookProps);

	const registry = useElementRegistry();
	const registrationRef = useRef<(() => void) | null>(null);
	const draggableRef = useCallback(
		(element: HTMLAnchorElement | HTMLButtonElement | null) => {
			if (!element) {
				registrationRef.current?.();
				registrationRef.current = null;
				draggableAnchorRef.current = null;
				draggableButtonRef.current = null;
				return;
			}
			if (element instanceof HTMLAnchorElement) {
				draggableAnchorRef.current = element;
			} else {
				draggableButtonRef.current = element;
			}
			if (registrationRef.current) {
				registrationRef.current();
			}
			registrationRef.current = registry.registerL1Item({
				id,
				element,
			});
		},
		[id, registry, draggableAnchorRef, draggableButtonRef, registrationRef],
	);

	const isDragging = !!itemIsDraggable && dragState.type === 'dragging';
	const expandableClosedForDrag = useRef(false);

	useLayoutEffect(() => {
		if (!isExpandable || !itemIsDraggable) {
			return;
		}
		if (isDragging && isExpandOrFlyoutOpen) {
			onExpandFlyoutToggle(false);
			expandableClosedForDrag.current = true;
		} else if (!isDragging && expandableClosedForDrag.current) {
			onExpandFlyoutToggle(true);
			expandableClosedForDrag.current = false;
		}
	}, [
		isDragging,
		isExpandable,
		isExpandOrFlyoutOpen,
		itemIsDraggable,
		expandableClosedForDrag,
		onExpandFlyoutToggle,
	]);

	return useMemo(
		() => ({
			draggableRef: itemIsDraggable ? draggableRef : undefined,
			isItemDraggable: itemIsDraggable,
			isItemDroppable: itemIsDroppable,
			isDragging,
			dropTargetRef: itemIsDroppable ? dropTargetRef : undefined,
			dropIndicator: itemIsDroppable ? dropIndicator : undefined,
			maybeDragPreview: itemIsDraggable ? dragPreview : null,
		}),
		[
			draggableRef,
			dragPreview,
			dropIndicator,
			dropTargetRef,
			isDragging,
			itemIsDraggable,
			itemIsDroppable,
		],
	);
}

function useItemDragAndDropOLD<T extends ItemData>(props: Props<T>): ItemDragAndDrop {
	const {
		data,
		item,
		id,
		isExpandable,
		isItemDraggable,
		isItemDroppable,
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		isDragAndDropEnabled,
		group,
		isItemDataInGroup,
		isItemData,
		getItemData,
	} = props;
	const itemIsDraggable = isItemDraggable?.(data) ?? !!isDragAndDropEnabled;
	const itemIsDroppable = isItemDroppable?.(data) ?? !!isDragAndDropEnabled;

	const {
		draggableAnchorRef,
		draggableButtonRef,
		state: dragState,
		dragPreview,
		dropTargetRef,
		dropIndicator,
	} = useMenuItemDragAndDrop({
		...(isDragAndDropEnabled && {
			...(itemIsDraggable
				? {
						draggable: {
							getInitialData: () => getItemData({ ...data, menuItemId: id, group } as T),
							getDragPreviewPieces: () => ({
								elemBefore: getElemBeforeForItem(item),
								content: getDragPreviewContentForItem(item, data?.label),
							}),
							canDrag: () => {
								return isItemDraggable ? isItemDraggable(data) : !!isDragAndDropEnabled;
							},
						},
					}
				: {}),
			...(itemIsDroppable
				? {
						dropTarget: {
							getData: (args) => {
								const { source } = args;
								return getItemData({
									...data,
									menuItemId: id,
									group: source?.data?.group as ItemDragAndDropGroup,
								} as T);
							},
							getOperations: () => ({
								'reorder-before': itemIsDraggable ? 'available' : undefined,
								'reorder-after': 'available',
							}),
							canDrop: (args) => {
								const { source } = args;

								const canDrop =
									isItemData(source.data) &&
									source.data.menuItemId !== id &&
									isItemDataInGroup(source.data, group);

								return canDrop;
							},
						},
					}
				: {}),
		}),
	});

	const registry = useElementRegistry();
	const registrationRef = useRef<(() => void) | null>(null);
	const draggableRef = useCallback(
		(element: HTMLAnchorElement | HTMLButtonElement | null) => {
			if (!element) {
				registrationRef.current?.();
				registrationRef.current = null;
				draggableAnchorRef.current = null;
				draggableButtonRef.current = null;
				return;
			}
			if (element instanceof HTMLAnchorElement) {
				draggableAnchorRef.current = element;
			} else {
				draggableButtonRef.current = element;
			}
			if (registrationRef.current) {
				registrationRef.current();
			}
			registrationRef.current = registry.registerL1Item({
				id,
				element,
			});
		},
		[id, registry, draggableAnchorRef, draggableButtonRef, registrationRef],
	);

	const isDragging = !!itemIsDraggable && dragState.type === 'dragging';
	const expandableClosedForDrag = useRef(false);

	useLayoutEffect(() => {
		if (!isExpandable || !itemIsDraggable) {
			return;
		}
		if (isDragging && isExpandOrFlyoutOpen) {
			onExpandFlyoutToggle(false);
			expandableClosedForDrag.current = true;
		} else if (!isDragging && expandableClosedForDrag.current) {
			onExpandFlyoutToggle(true);
			expandableClosedForDrag.current = false;
		}
	}, [
		isDragging,
		isExpandable,
		isExpandOrFlyoutOpen,
		itemIsDraggable,
		expandableClosedForDrag,
		onExpandFlyoutToggle,
	]);

	return {
		draggableRef: itemIsDraggable ? draggableRef : undefined,
		isItemDraggable: itemIsDraggable,
		isItemDroppable: itemIsDroppable,
		isDragging,
		dropTargetRef: itemIsDroppable ? dropTargetRef : undefined,
		dropIndicator: itemIsDroppable ? dropIndicator : undefined,
		maybeDragPreview: itemIsDraggable ? dragPreview : null,
	};
}
